// MutuiClientSection Component - Extracted from Clients.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { Home, Pencil, Trash2, ChevronRight } from 'lucide-react';

// Stati della pratica con colori
const statoConfig = {
  analisi_preliminare: { label: 'Analisi Preliminare', color: 'bg-amber-100 text-amber-800 border-amber-300' },
  inviata_banca: { label: 'Inviata in Banca', color: 'bg-blue-100 text-blue-800 border-blue-300' },
  erogata: { label: 'Erogata', color: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  rifiutata: { label: 'Rifiutata', color: 'bg-red-100 text-red-800 border-red-300' }
};

const formatCurrency = (value) => {
  if (!value) return '-';
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value);
};

const MutuiClientSection = ({ clientId, API, onRefresh }) => {
  const [clientMutui, setClientMutui] = useState([]);
  const [loadingMutui, setLoadingMutui] = useState(true);
  
  // Modal states
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedMutuo, setSelectedMutuo] = useState(null);
  
  // Form state for editing
  const [editFormData, setEditFormData] = useState({
    indirizzo: '',
    prezzo_immobile: '',
    anticipo: '',
    importo_richiesto: '',
    durata_anni: '',
    banca: '',
    note: '',
    stato: 'analisi_preliminare'
  });
  
  // Google Maps autocomplete ref
  const addressInputRef = useRef(null);
  const autocompleteRef = useRef(null);
  
  const fetchMutui = useCallback(() => {
    if (clientId) {
      setLoadingMutui(true);
      axios.get(`${API}/mutui?client_id=${clientId}`)
        .then(res => {
          setClientMutui(res.data);
          setLoadingMutui(false);
        })
        .catch(() => setLoadingMutui(false));
    }
  }, [clientId, API]);
  
  useEffect(() => {
    fetchMutui();
  }, [fetchMutui]);
  
  // Initialize Google Maps Autocomplete when edit modal opens
  useEffect(() => {
    if (editModalOpen && addressInputRef.current && window.google?.maps?.places) {
      if (autocompleteRef.current) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
      
      autocompleteRef.current = new window.google.maps.places.Autocomplete(addressInputRef.current, {
        types: ['address'],
        componentRestrictions: { country: 'it' }
      });
      
      autocompleteRef.current.addListener('place_changed', () => {
        const place = autocompleteRef.current.getPlace();
        if (place.formatted_address) {
          setEditFormData(prev => ({ ...prev, indirizzo: place.formatted_address }));
        }
      });
    }
  }, [editModalOpen]);
  
  const openEditModal = (mutuo) => {
    setSelectedMutuo(mutuo);
    setEditFormData({
      indirizzo: mutuo.indirizzo || '',
      prezzo_immobile: mutuo.prezzo_immobile || '',
      anticipo: mutuo.anticipo || '',
      importo_richiesto: mutuo.importo_richiesto || '',
      durata_anni: mutuo.durata_anni || '',
      banca: mutuo.banca || '',
      note: mutuo.note || '',
      stato: mutuo.stato || 'analisi_preliminare'
    });
    setEditModalOpen(true);
  };
  
  const openDetailModal = (mutuo) => {
    setSelectedMutuo(mutuo);
    setDetailModalOpen(true);
  };
  
  const openDeleteDialog = (mutuo) => {
    setSelectedMutuo(mutuo);
    setDeleteDialogOpen(true);
  };
  
  const handleSaveEdit = async () => {
    if (!selectedMutuo) return;
    
    try {
      const payload = {
        client_id: clientId,
        indirizzo: editFormData.indirizzo,
        prezzo_immobile: editFormData.prezzo_immobile ? parseFloat(editFormData.prezzo_immobile) : null,
        anticipo: editFormData.anticipo ? parseFloat(editFormData.anticipo) : null,
        importo_richiesto: editFormData.importo_richiesto ? parseFloat(editFormData.importo_richiesto) : null,
        durata_anni: editFormData.durata_anni ? parseInt(editFormData.durata_anni) : null,
        banca: editFormData.banca,
        note: editFormData.note,
        stato: editFormData.stato
      };
      
      await axios.put(`${API}/mutui/${selectedMutuo.id}`, payload);
      toast.success('Mutuo/prestito aggiornato con successo');
      setEditModalOpen(false);
      fetchMutui();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante il salvataggio');
    }
  };
  
  const handleDelete = async () => {
    if (!selectedMutuo) return;
    
    try {
      await axios.delete(`${API}/mutui/${selectedMutuo.id}`);
      toast.success('Mutuo/prestito eliminato');
      setDeleteDialogOpen(false);
      setSelectedMutuo(null);
      fetchMutui();
    } catch (error) {
      toast.error('Errore durante l\'eliminazione');
    }
  };
  
  return (
    <div className="border-t pt-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-slate-900 flex items-center gap-2">
          <Home className="w-4 h-4 text-pink-600" />
          MUTUI/PRESTITI
        </h3>
      </div>
      {loadingMutui ? (
        <p className="text-sm text-slate-500 text-center py-4">Caricamento mutui/prestiti...</p>
      ) : clientMutui.length === 0 ? (
        <p className="text-sm text-slate-500 text-center py-4">Nessuna richiesta di mutuo/prestito per questo cliente</p>
      ) : (
        <div className="space-y-2">
          {clientMutui.map(mutuo => (
            <div key={mutuo.id} className="border rounded-lg p-3 bg-slate-50 border-slate-200" data-testid={`mutuo-item-${mutuo.id}`}>
              {/* Header Row */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center shadow-sm">
                    <Home className="w-5 h-5 text-pink-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">{formatCurrency(mutuo.importo_richiesto)}</p>
                  </div>
                  <Badge variant="outline" className={statoConfig[mutuo.stato]?.color || 'bg-slate-100'}>
                    {statoConfig[mutuo.stato]?.label || mutuo.stato}
                  </Badge>
                </div>
                
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="icon" onClick={() => openEditModal(mutuo)} title="Modifica mutuo/prestito">
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => openDeleteDialog(mutuo)} className="text-red-600 hover:text-red-700" title="Elimina mutuo/prestito">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => openDetailModal(mutuo)} 
                    title="Dettagli completi"
                    className="px-3 h-8 bg-slate-100 hover:bg-slate-200 border-slate-300"
                  >
                    <span className="text-xs text-slate-600 mr-1">Dettagli</span>
                    <ChevronRight className="w-4 h-4 text-slate-500" />
                  </Button>
                </div>
              </div>
              
              {/* Detail Row */}
              <div className="bg-white rounded border border-slate-100 p-3">
                <div className="grid grid-cols-5 gap-6">
                  <div className="flex flex-col">
                    <span className="text-xs text-slate-500 mb-1">Banca</span>
                    <span className="font-medium text-sm text-slate-900">{mutuo.banca || '-'}</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs text-slate-500 mb-1">Durata</span>
                    <span className="font-medium text-sm text-slate-900">{mutuo.durata_anni ? `${mutuo.durata_anni} anni` : '-'}</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs text-slate-500 mb-1">Prezzo Immobile</span>
                    <span className="font-medium text-sm text-slate-900">{formatCurrency(mutuo.prezzo_immobile)}</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs text-slate-500 mb-1">Anticipo</span>
                    <span className="font-medium text-sm text-slate-900">{formatCurrency(mutuo.anticipo)}</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs text-slate-500 mb-1">Data Richiesta</span>
                    <span className="font-medium text-sm text-slate-900">{new Date(mutuo.created_at).toLocaleDateString('it-IT')}</span>
                  </div>
                </div>
                {mutuo.indirizzo && (
                  <div className="mt-2 pt-2 border-t border-slate-100">
                    <span className="text-xs text-slate-500">Immobile: </span>
                    <span className="text-sm text-slate-700">{mutuo.indirizzo}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Edit Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Home className="w-5 h-5 text-pink-600" />
              Modifica Mutuo/Prestito
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium">Indirizzo Immobile</Label>
              <Input
                ref={addressInputRef}
                placeholder="Inserisci indirizzo..."
                value={editFormData.indirizzo}
                onChange={(e) => setEditFormData(prev => ({ ...prev, indirizzo: e.target.value }))}
              />
              <p className="text-xs text-slate-500">Inizia a digitare per l'autocompletamento</p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Prezzo Immobile (€)</Label>
                <Input
                  type="number"
                  placeholder="es. 250000"
                  value={editFormData.prezzo_immobile}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, prezzo_immobile: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Anticipo / Acconto (€)</Label>
                <Input
                  type="number"
                  placeholder="es. 50000"
                  value={editFormData.anticipo}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, anticipo: e.target.value }))}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Importo Richiesto (€)</Label>
                <Input
                  type="number"
                  placeholder="es. 200000"
                  value={editFormData.importo_richiesto}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, importo_richiesto: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Durata (Anni)</Label>
                <Select 
                  value={editFormData.durata_anni?.toString() || ''} 
                  onValueChange={(value) => setEditFormData(prev => ({ ...prev, durata_anni: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona durata" />
                  </SelectTrigger>
                  <SelectContent>
                    {[5, 10, 15, 20, 25, 30, 35, 40].map(anni => (
                      <SelectItem key={anni} value={anni.toString()}>{anni} anni</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">Banca</Label>
              <Input
                placeholder="es. Intesa Sanpaolo, UniCredit..."
                value={editFormData.banca}
                onChange={(e) => setEditFormData(prev => ({ ...prev, banca: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">Stato Pratica</Label>
              <Select 
                value={editFormData.stato} 
                onValueChange={(value) => setEditFormData(prev => ({ ...prev, stato: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="analisi_preliminare">
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                      Analisi Preliminare
                    </span>
                  </SelectItem>
                  <SelectItem value="inviata_banca">
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                      Inviata in Banca
                    </span>
                  </SelectItem>
                  <SelectItem value="erogata">
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                      Erogata
                    </span>
                  </SelectItem>
                  <SelectItem value="rifiutata">
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-red-500"></span>
                      Rifiutata
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">Note</Label>
              <Textarea
                placeholder="Dettagli aggiuntivi o motivi del rifiuto..."
                value={editFormData.note}
                onChange={(e) => setEditFormData(prev => ({ ...prev, note: e.target.value }))}
                rows={3}
              />
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setEditModalOpen(false)}>
                Annulla
              </Button>
              <Button onClick={handleSaveEdit} className="bg-pink-600 hover:bg-pink-700">
                Salva Modifiche
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Detail Modal */}
      <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Home className="w-5 h-5 text-pink-600" />
              Dettagli Mutuo/Prestito
            </DialogTitle>
          </DialogHeader>
          
          {selectedMutuo && (
            <div className="space-y-4 mt-4">
              <div className="flex items-center justify-between">
                <span className="text-2xl font-bold text-slate-900">{formatCurrency(selectedMutuo.importo_richiesto)}</span>
                <Badge variant="outline" className={`text-sm ${statoConfig[selectedMutuo.stato]?.color || 'bg-slate-100'}`}>
                  {statoConfig[selectedMutuo.stato]?.label || selectedMutuo.stato}
                </Badge>
              </div>
              
              <div className="grid grid-cols-2 gap-4 bg-slate-50 p-4 rounded-lg">
                <div>
                  <p className="text-xs text-slate-500">Banca</p>
                  <p className="font-medium">{selectedMutuo.banca || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Durata</p>
                  <p className="font-medium">{selectedMutuo.durata_anni ? `${selectedMutuo.durata_anni} anni` : '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Prezzo Immobile</p>
                  <p className="font-medium">{formatCurrency(selectedMutuo.prezzo_immobile)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Anticipo</p>
                  <p className="font-medium">{formatCurrency(selectedMutuo.anticipo)}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-xs text-slate-500">Data Richiesta</p>
                  <p className="font-medium">{new Date(selectedMutuo.created_at).toLocaleString('it-IT')}</p>
                </div>
                {selectedMutuo.indirizzo && (
                  <div className="col-span-2">
                    <p className="text-xs text-slate-500">Indirizzo Immobile</p>
                    <p className="font-medium">{selectedMutuo.indirizzo}</p>
                  </div>
                )}
                {selectedMutuo.note && (
                  <div className="col-span-2">
                    <p className="text-xs text-slate-500">Note</p>
                    <p className="font-medium text-slate-700">{selectedMutuo.note}</p>
                  </div>
                )}
              </div>
              
              <div className="flex justify-end">
                <Button variant="outline" onClick={() => setDetailModalOpen(false)}>
                  Chiudi
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Conferma Eliminazione</AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicuro di voler eliminare questa richiesta di mutuo/prestito?
              <br />
              <strong>Importo: {formatCurrency(selectedMutuo?.importo_richiesto)}</strong>
              <br />
              Questa azione non può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default MutuiClientSection;
